import { addDocumentAssetsFromOrigin, computeHashBase64Web, InlineDocumentAsset } from '../inline-document';
import { addressToV6Strings, emptyAddress } from '../../../../webmodule-common/other/ui/maps/map-helpers';
import {
  applyV6PricesToQuoteItemPrice,
  applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections,
  getV6QuoteItemForContainer
} from './helper-functions';
import { emptyGuid, newGuid } from '../../../../webmodule-common/other/api/guid';
import { flagEquals } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { getGroups, getItemGroupId, normalizeBatchDate } from '../quote-item-grouping';
import {
  isDefaultShipping,
  isFrame,
  isSpecialItem,
  isSSI,
  isSupplierFrameItem,
  serverSideShippingItem
} from '../quote-helper-functions';
import { LangV6SsiProcessor } from '../../../../localization/scripts/quotes/v6-ssi-processor-lang';
import { localDateToServer } from '../../../../webmodule-common/other/datetime-converter';
import { money } from '../../../../webmodule-common/other/currency-formatter';
import { quoteItemContentType } from '../default-quote-item-content-type';
import { QuoteItemRowAction, QuoteItemState, QuoteItemType } from '../../../api/dealer-api-interface-quote';
import { supplierQuoteItemContentType } from '../supplier-quote-item-content-type';
import { toJsonStr } from '../../../../webmodule-common/other/blob-converters';
import {
  v6Config,
  v6ConfigSupplierOptions,
  V6ServerSideSpecialItem
} from '../../../../webmodule-common/interop/interfaces/v6-config';
import type {
  InputUpdateQuoteItems,
  QuoteItem,
  QuoteItemPrice,
  QuoteItemPriceBreakdown
} from '../../../api/dealer-api-interface-quote';
import type { QuoteContainerManager } from '../quote-container';
import type {
  QuoteContract,
  QuoteFrameItemPrice,
  QuoteItemBaseContract,
  QuoteShippingInformation,
  RequestGetSSI,
  V6SsiSupplierNote
} from '../../../../webmodule-common/interop/webmodule-interop';
import type { QuoteItemContainer } from '../quote-item-container';
import type { SSIProcessor } from '../ssi-processor';

export class V6SSIProcessor implements SSIProcessor {
  quoteManager: QuoteContainerManager;
  excludedItemFilter: string[];
  extraItems: QuoteItemContainer[] | undefined;

  /**
   * Batch membership the quote is ABOUT to have, keyed by quote item id (value null = ungrouped).
   * Overrides what the grouping document currently says.
   *
   * SSI must be generated in the shape the quote will be in once the save completes, not the shape it
   * is in while the editor is still open. The item editor persists an item first and only then moves it
   * into its batch (the move needs the item's server record version), so at generation time the document
   * still has the item ungrouped - and V6 would compute the batch's per-batch charge without it.
   *
   * Keyed by `item.id`, not commonId: a brand-new item's commonId is minted by the server on insert, so
   * it is not yet known here, whereas the id is client-assigned and is what the contract already sends
   * as quoteItemCommonId.
   */
  pendingBatchAssignments: ReadonlyMap<string, string | null> | undefined;

  constructor(
    qm: QuoteContainerManager,
    itemFilter?: string[],
    extraItems?: QuoteItemContainer[],
    pendingBatchAssignments?: ReadonlyMap<string, string | null>
  ) {
    this.extraItems = extraItems;
    this.quoteManager = qm;
    this.excludedItemFilter = itemFilter ?? [];
    this.pendingBatchAssignments = pendingBatchAssignments;
  }

  /**
   *
   * @returns a QuoteContract that only contains the frame items needed for the generation and processing of
   * SSI Freehand items or special items added that are for the supplier to create
   */
  private createQuoteContractForSSIGeneration(): QuoteContract {
    const qm = this.quoteManager;
    // Grouping doc drives MRD batch membership on the SSI send (Task 230006). Null when unbatched /
    // not yet loaded -> no batches, pre-MRD behaviour. Only order-bound items reach the contract, so a
    // referenced group is automatically a non-excluded, order-bound batch (Q2).
    // Gated on the supplier's V6 supporting batches: with the gate off we deliberately send the pre-MRD
    // contract (no batches[], no quoteItemBatchId) so V6 never creates quote item batches, and groups
    // stay a Dealer-only organisation device.
    const doc = qm.isV6BatchLinkingEnabled ? qm.quoteItemGrouping : null;

    // The batch an item will be in once this save lands: a caller-supplied pending assignment wins over
    // the document, which has not been updated yet. Still gated on `doc` - with batch linking off no item
    // may carry a batchId at all, so a pending assignment can never smuggle one into a pre-MRD contract.
    const pending = this.pendingBatchAssignments;
    const batchIdFor = (itemId: string, commonId: string): string | null => {
      if (!doc) return null;
      const staged = pending?.get(itemId);
      return staged !== undefined ? staged : getItemGroupId(doc, commonId);
    };

    const items = (): QuoteItemBaseContract[] => {
      const result: QuoteItemBaseContract[] = [];
      qm.container.items?.forEach(quoteItem => {
        if (!this.excludedItemFilter.includes(quoteItem.id)) {
          const qic = qm.quoteItemContainer(quoteItem.id);
          addQuoteItemContainer(qic, result);
        }
      });
      this.extraItems?.forEach(itemContainer => {
        addQuoteItemContainer(itemContainer, result);
      });

      return result;
    };

    const quoteItems = items();

    // Batches actually referenced by an order-bound item on this send. The macro reads quote\batches +
    // quoteItem.quoteItemBatchId to compute a per-batch charge (e.g. shipping per delivery date).
    const referencedBatchIds = new Set(quoteItems.map(i => i.quoteItemBatchId).filter((id): id is string => !!id));
    const batches = doc
      ? getGroups(doc)
          .filter(g => referencedBatchIds.has(g.id))
          .map(g => ({
            id: g.id,
            description: g.name,
            deliveryDate: normalizeBatchDate(g.attributes.deliveryDate),
            customerRef: (g.attributes.customerRef as string | null) ?? null,
            comment: (g.attributes.comment as string | null) ?? null,
            state: (g.attributes.status as string | null) ?? null
          }))
      : undefined;

    return {
      comment: '',
      deliveryDate: localDateToServer(new Date()), // not used
      quoteCommonId: qm.quote.id,
      quoteItems,
      batches: batches && batches.length > 0 ? batches : null,
      shipToAddress: addressToV6Strings(qm.quote.shippingAddress ?? emptyAddress()),
      supplierId: qm.quote.supplierId,
      supplierQuoteData: toJsonStr(qm.quoteProviderData()) ?? null,
      terms1: null,
      terms2: null,
      terms3: null,
      terms4: null,
      terms5: null,
      title: qm.quote.title
    } satisfies QuoteContract;

    function addQuoteItemContainer(qic: QuoteItemContainer, result: QuoteItemBaseContract[]) {
      if (isFrame(qic.item) || isSpecialItem(qic.item)) {
        let providerData: string | null = null;
        //This is using internal knowledge of the V6 data structure.
        //we probably want to expose this at some point
        const raw = getV6QuoteItemForContainer(qic);
        if (raw) {
          (raw as any).bomView = undefined;
          (raw as any).vrml = undefined;
        }
        providerData = toJsonStr(raw) ?? null;
        addQuoteItemContainerToResult(qic, providerData, result);
      }
    }

    function addQuoteItemContainerToResult(
      qic: QuoteItemContainer,
      providerData: string | null,
      result: QuoteItemBaseContract[]
    ) {
      const item = qic.item;
      const contractItem: QuoteItemBaseContract = {
        comments: item.comment ?? '',
        description: item.description,
        itemType: isSupplierFrameItem(item)
          ? supplierQuoteItemContentType.CID_FRAM
          : supplierQuoteItemContentType.CID_FREEHAND,
        itemSubType: item.quoteItemContentType,
        price: qic.price.quantityCost,
        grossPrice: qic.price.supplierGrossQuantityCost,
        quantity: item.quantity,
        quoteItemCommonId: item.id,
        supplierData: isFrame(item) || isSpecialItem(item) ? providerData : null,
        title: item.title,
        // MRD batch membership as it will be after this save (Task 230006): a pending assignment from
        // the caller, else the grouping doc keyed by commonId. Null when ungrouped.
        quoteItemBatchId: batchIdFor(item.id, item.commonId)
      };
      result.push(contractItem);
    }
  }

  private async createShippingInformation(): Promise<QuoteShippingInformation> {
    return {
      address: this.quoteManager.quote.shippingAddress ?? emptyAddress(),
      installationAddress: (await this.quoteManager.getInstallationAddress()) ?? emptyAddress()
    };
  }

  private async buildSSIRequest(): Promise<RequestGetSSI> {
    return {
      quote: this.createQuoteContractForSSIGeneration(),
      shippingInformation: await this.createShippingInformation(),
      supplierReferenceOverrideKey: this.quoteManager.quote.quoteOwnerId,
      additionalData: null
    };
  }

  private collectExistingSSIFromQuote() {
    return this.quoteManager.container?.items?.filter(item => isSSI(item)) ?? [];
  }

  public async processSSI() {
    const supplierOptions = v6ConfigSupplierOptions(this.quoteManager.quote.supplierId);
    const getInput = async () => {
      const ssiRequest = await this.buildSSIRequest();
      const ssi = await v6Config().getQuoteSSI(ssiRequest);
      const existingssi = this.collectExistingSSIFromQuote();
      this.quoteManager.saveQuoteCalculations(ssi.quoteCalculations);
      this.quoteManager.saveQuoteAnnotations(ssi.annotations);

      //find new or different documents or create document deletion map
      const newDocs = addDocumentAssetsFromOrigin(
        this.quoteManager.quote.assetVirtualPaths ?? {},
        (await this.supplierNotesToInlineDocuments(ssi.notes?.filter(x => !x.quoteItemId))) ?? [],
        ['ssi']
      );
      //push updates for next quoteSave Event
      this.quoteManager.updatePendingQuoteAssets(newDocs);

      const quoteItemNotes = ssi.notes?.filter(x => x.quoteItemId) ?? [];

      const shippingLine = this.quoteManager.container?.items?.find(x => isDefaultShipping(x));

      // Per-batch SSI (MRD, Task 230006): SSI identity is (quoteItemContentType, batchId), not
      // content-type alone, so each delivery batch can carry its own copy of a charge (e.g. one shipping
      // charge per delivery date). A null/empty batchId is a quote-level SSI = pre-MRD behaviour. An
      // existing SSI item's batch comes from the link-table membership (via the container), NOT the item
      // row (which no longer carries it); a returned SSI item's batch is its `batchId` field.
      // Gated on the supplier supporting V6-linked batches: with the gate off we do not accept batch SSI
      // at all - every batchId is ignored, restoring the pre-MRD "one SSI per content-type per quote"
      // invariant, so a stray batchId from V6 cannot fan a charge out into per-group duplicates.
      const batchLinking = this.quoteManager.isV6BatchLinkingEnabled;
      const ssiKey = (contentType: number, batchId: string | null | undefined) =>
        `${contentType}::${(batchLinking ? batchId : null) ?? ''}`;
      const ssiItemKey = (x: V6ServerSideSpecialItem) => ssiKey(x.quoteItemContentType, x.batchId);
      const quoteItemKey = (item: QuoteItem) =>
        ssiKey(item.quoteItemContentType, this.quoteManager.itemBatchId(item.commonId));

      const returnedKeys = new Set(ssi.items.map(ssiItemKey));
      const existingKeys = new Set(existingssi.map(quoteItemKey));
      const deletedSSI = existingssi.filter(item => !returnedKeys.has(quoteItemKey(item)));
      const updatedSSI = existingssi.filter(item => returnedKeys.has(quoteItemKey(item)));
      const insertedSSI = ssi.items.filter(ssiItem => !existingKeys.has(ssiItemKey(ssiItem)));
      const user = getCurrentUser();
      if (!user) return; //cant happen, nullable fix

      const input: InputUpdateQuoteItems = {
        items: []
      };

      if (this.quoteManager.isPricingMetricLockedBySupplier) {
        const markup = ssi.marginMarkupOverride;
        if (markup) {
          this.quoteManager.applySupplierMarginMarkupOverride(markup.value, markup.valueType);
          await this.quoteManager.updatePricingRules();
        }
      }

      //Process Quote and Quote Item Notes
      const notesByItemId = new Map<string, InlineDocumentAsset[]>();

      for (const note of quoteItemNotes) {
        if (!note.quoteItemId) continue;
        if (!notesByItemId.has(note.quoteItemId)) {
          notesByItemId.set(note.quoteItemId, []);
        }
        notesByItemId.get(note.quoteItemId)!.push(await this.supplierNoteToInlineDocument(note));
      }

      for (const [quoteItemId, notes] of notesByItemId.entries()) {
        const qic = this.quoteManager.quoteItemContainer(quoteItemId);
        // Diff the item's notes against the ITEM's own asset map. Using the quote's map here would
        // both emit deletes for keys that belong to another scope (dropping same-named item assets)
        // and silently discard a note whose hash happens to match a quote level document.
        const newDocs = addDocumentAssetsFromOrigin(qic.data?.assetVirtualPaths ?? {}, notes, ['ssi']);

        const hasNewDocs = Object.keys(newDocs).length > 0;

        if (hasNewDocs) {
          input.items.push({
            action: QuoteItemRowAction.Update,
            input: {
              assets: newDocs,
              providerDataDifference: null,
              quoteItem: qic.item,
              quoteItemBuyInData: { data: qic.buyInData ?? null },
              quoteItemPrice: qic.price
            }
          });
        }
      }
      //End of processing Quote and Quote Item Notes

      const processBreakdowns = (qty: number, price: QuoteItemPrice, ssiItem: V6ServerSideSpecialItem) => {
        price.breakdowns = null;
        if (ssiItem.unitPriceBreakdown && ssiItem.unitPriceBreakdown.length > 0) {
          price.breakdowns = ssiItem.unitPriceBreakdown.map(x => {
            const b: QuoteItemPriceBreakdown = {
              name: x.name,
              unitPrice: money(x.unitPrice, 2),
              subType: x.subType,
              calculatedGrossSellingPrice: 0,
              calculatedNetSellingPrice: 0,
              quantityCost: 0,
              singleUnitCost: 0,
              sourceData: {},
              supplierGrossQuantityCost: 0,
              supplierGrossSingleUnitCost: 0,
              supplierNettQuantityCost: 0,
              supplierNettSingleUnitCost: 0
            };
            applyV6PricesToQuoteItemPrice(qty, x.sourceData as QuoteFrameItemPrice, b);
            return b;
          });
        }
      };

      deletedSSI.forEach(item => {
        input.items.push({
          action: QuoteItemRowAction.HardDelete,
          input: {
            assets: null,
            providerDataDifference: null,

            quoteItem: item,
            quoteItemBuyInData: null,
            quoteItemPrice: null
          }
        });
      });

      for (const item of updatedSSI) {
        const updatedSSIItem = ssi.items.find(x => ssiItemKey(x) === quoteItemKey(item));
        if (!updatedSSIItem) return; //cant happen, nullable fix

        item.comment = updatedSSIItem.comment;
        item.description = updatedSSIItem.description;
        item.title = updatedSSIItem.title;
        item.quantity = updatedSSIItem.quantity;
        const qic = this.quoteManager.quoteItemContainer(item.id);
        const data = qic.data;
        const newDocs = addDocumentAssetsFromOrigin(
          data?.assetVirtualPaths ?? {},
          (await this.supplierNotesToInlineDocuments(updatedSSIItem.notes)) ?? [],
          ['ssi']
        );

        const price = qic.price;
        if (price) {
          if (updatedSSIItem.sourceData) {
            applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
              item.quantity,
              updatedSSIItem.sourceData,
              price
            );
          } else {
            //This is legacy code. from macro v8
            price.singleUnitCost = money(updatedSSIItem.unitPrice, 2);
            price.quantityCost = money(updatedSSIItem.unitPrice * item.quantity, 2);
            price.supplierGrossQuantityCost = price.quantityCost;
            price.supplierGrossSingleUnitCost = price.singleUnitCost;
            price.supplierNettQuantityCost = price.quantityCost;
            price.supplierNettSingleUnitCost = price.singleUnitCost;
          }
          processBreakdowns(item.quantity, price, updatedSSIItem);
        }
        input.items.push({
          action: QuoteItemRowAction.Update,
          input: {
            assets: newDocs,
            providerDataDifference: null,
            quoteItem: item,
            quoteItemBuyInData: null,
            quoteItemPrice: price
          }
        });
      }
      const createDate = localDateToServer(new Date());
      const userId = user.id;

      for (const newSSIItem of insertedSSI) {
        const newId = newGuid();
        const item: QuoteItem = {
          isRestrictedToPowerUser: false,
          comment: newSSIItem.comment,
          // commonId == id on first creation, and the server now honours BOTH (TaskCreateQuoteItem) rather
          // than minting its own CommonId - so this is the identity the grouping document, the presentation
          // order and the batch links will really use, known here before the insert.
          commonId: newId,
          dateCreated: createDate, //ignored
          description: newSSIItem.description,
          id: newId,
          itemType: QuoteItemType.Basic, //Provider Type is ONLY for items with serviceProvider and Data
          lastModifiedDate: createDate,
          lastModifiedUserId: userId,
          providerReferenceId: emptyGuid,
          quantity: newSSIItem.quantity,
          quoteId: this.quoteManager.quote.id,
          quoteItemContentType: newSSIItem.quoteItemContentType,
          recordVersion: '',
          serviceProvider: '',
          title: newSSIItem.title,
          virtualThumbnailPath: '',
          commentToManufacturer: null,
          isShippingItem: false,

          state: QuoteItemState.Active
        };
        // Per-batch SSI (MRD, 230006): record the new charge's batch NOW, by commonId, so the grouping
        // document places it before the row is ever rendered. Blank = quote-level. This is what removed the
        // visible two-phase, where the charge painted at the bottom of the quote and then jumped up into its
        // batch once its placement caught up.
        const newBatchId = (batchLinking ? newSSIItem.batchId : null) ?? null;
        this.quoteManager.setItemBatch(newId, newBatchId);

        // Also staged against the item id, as a correction rather than a duplicate: if the insert ever comes
        // back with a CommonId other than the one minted here, the placement above is keyed on an identity
        // that does not exist and the staged entry re-places it under the real one (processResultItem).
        this.quoteManager.stageItemBatchByItemId(newId, newBatchId);
        const newDocs = addDocumentAssetsFromOrigin(
          {},
          (await this.supplierNotesToInlineDocuments(newSSIItem.notes)) ?? [],
          ['ssi']
        );

        //initialize a new price object
        const price: QuoteItemPrice = {
          id: item.id,
          singleUnitCost: money(newSSIItem.unitPrice, 2),
          quantityCost: money(newSSIItem.unitPrice * item.quantity, 2), //DNU
          supplierGrossSingleUnitCost: money(newSSIItem.unitPrice, 2),
          supplierNettSingleUnitCost: money(newSSIItem.unitPrice, 2),
          calculatedNetSellingPrice: 0, //DNU
          dateCreated: createDate, //DNU
          percentMarginOrMarkup: 0, //DNU
          calculatedGrossSellingPrice: 0, //DNU
          priceAdjustment: 0, //DNU
          recordVersion: '', //DNU
          requiresRepricing: false, //DNU
          supplierGrossQuantityCost: 0, //DNU
          supplierNettQuantityCost: 0, //DNU
          supplierPriceAdjustment: 0, //DNU,
          supplierPriceAdjustmentEnabled: false,
          supplierPriceAdjustmentReason: null,
          sourceData: {},
          isTaxableItem: this.quoteManager.quote.isShippingTaxable,
          breakdowns: null
        };

        //V9 Macros up, we use consistent pricing and source data attachment
        if (newSSIItem.sourceData) {
          applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
            newSSIItem.quantity,
            newSSIItem.sourceData,
            price
          );
        }
        processBreakdowns(item.quantity, price, newSSIItem);

        input.items.push({
          action: QuoteItemRowAction.Insert,
          input: {
            assets: newDocs,
            providerDataDifference: null,
            quoteItem: item,
            quoteItemBuyInData: null,
            quoteItemPrice: price
          }
        });
      }

      const hasSSIShipping = input.items.some(
        x =>
          isSSI(x.input.quoteItem, serverSideShippingItem) &&
          !flagEquals(x.action, QuoteItemRowAction.Delete, QuoteItemRowAction.HardDelete)
      );
      if ((hasSSIShipping && shippingLine) || (!hasSSIShipping && shippingLine && !supplierOptions.includeShipping)) {
        input.items.push({
          action: QuoteItemRowAction.HardDelete,
          input: {
            assets: null,
            providerDataDifference: null,

            quoteItem: shippingLine,
            quoteItemBuyInData: null,
            quoteItemPrice: null
          }
        });
      }
      if (!hasSSIShipping && !shippingLine && supplierOptions.includeShipping) {
        const newId = newGuid();
        const item: QuoteItem = {
          isRestrictedToPowerUser: false,
          comment: '',
          commonId: newId, //ignored
          dateCreated: createDate, //ignored
          description: LangV6SsiProcessor.totalShippingCost(),
          id: newId, //ignored
          itemType: QuoteItemType.Basic,
          lastModifiedDate: createDate,
          lastModifiedUserId: userId,
          providerReferenceId: emptyGuid,
          quantity: 1,
          quoteId: this.quoteManager.quote.id,
          quoteItemContentType: quoteItemContentType.shipping,
          recordVersion: '',
          serviceProvider: '',
          title: LangV6SsiProcessor.shipping(),
          virtualThumbnailPath: '',
          commentToManufacturer: null,
          isShippingItem: false,

          state: QuoteItemState.Active
        };

        input.items.push({
          action: QuoteItemRowAction.Insert,
          input: {
            assets: null,
            providerDataDifference: null,
            quoteItem: item,
            quoteItemBuyInData: null,
            quoteItemPrice: {
              breakdowns: null,
              id: item.id,
              singleUnitCost: money(0, 2),
              quantityCost: money(0, 2), //DNU
              supplierGrossSingleUnitCost: money(0, 2),
              supplierNettSingleUnitCost: money(0, 2),
              calculatedNetSellingPrice: 0, //DNU
              dateCreated: createDate, //DNU
              percentMarginOrMarkup: 0, //DNU
              calculatedGrossSellingPrice: 0, //DNU
              priceAdjustment: 0, //DNU
              recordVersion: '', //DNU
              requiresRepricing: false, //DNU
              supplierGrossQuantityCost: 0, //DNU
              supplierNettQuantityCost: 0, //DNU
              supplierPriceAdjustment: 0, //DNU
              supplierPriceAdjustmentEnabled: false,
              supplierPriceAdjustmentReason: null,
              sourceData: {},
              isTaxableItem: this.quoteManager.quote.isShippingTaxable
            }
          }
        });
      }
      // eslint-disable-next-line consistent-return
      return input;
    };

    return await getInput();
  }

  private async supplierNoteToInlineDocument(note: V6SsiSupplierNote): Promise<InlineDocumentAsset> {
    const inlineDoc: InlineDocumentAsset = {
      content: note.content,
      contentType: note.noteType,
      origin: `ssi`,
      name: note.name,
      hash: await computeHashBase64Web(note.content)
    };
    return inlineDoc;
  }
  private async supplierNotesToInlineDocuments(
    notes: V6SsiSupplierNote[] | null | undefined
  ): Promise<InlineDocumentAsset[] | null> {
    if (!notes) return null;
    const result: InlineDocumentAsset[] = [];
    for (const note of notes) {
      result.push(await this.supplierNoteToInlineDocument(note));
    }
    return result;
  }
}
